import { useEffect } from "react";
import { PaxProvider, PaxSnackbarProvider } from "@ncino/pax-ui";
import { paxCombinedTheme } from "@/theme";
import { initializeRouteTracking, isDatadogInitialized } from "@/services/monitoring";
import sharedLocale from "@/shared/locales/en_US.json";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { routeTree } from "./generated/routeTree.gen";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./services/graphql/queryUtil";
import { AuthProvider, useAuth } from "./services/auth/auth";
import type { TFunction } from "i18next";
import { NotFoundPage } from "./shared/components/NotFoundPage";
import {
  MarketplaceAccessDeniedError,
  ErrorComponentProps,
  UnauthorizedError,
} from "./shared/errors";
import { UnauthorizedPage } from "./shared/components/UnauthorizedPage";
import { MarketplaceAccessDeniedPage } from "./shared/components/MarketplaceAccessDeniedPage";
import "./registerPageLocales";
import { LoadingPageComponent } from "@/shared/components/LoadingPage";
import { useUserDataQuery } from "@/services/graphql/reactQuery";
import GlobalStyles from "@mui/material/GlobalStyles";
import MainContentSnackbarAlert, {
  SidebarSlideTransition,
} from "@/shared/components/SidebarAlertSnackbar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Icon } from "@phosphor-icons/react";

export interface StaticDataBreadcrumbsTitleFunctionParams {
  /**
   * Route params
   */
  params: Record<string, string>;
  /**
   * Data from the routes loader function
   */
  loaderData: unknown;
  /**
   * The i18next translation function
   */
  t: TFunction<"translation">;
}

function DefaultErrorComponent(props: ErrorComponentProps) {
  if (props.error instanceof MarketplaceAccessDeniedError) {
    return <MarketplaceAccessDeniedPage {...props} />;
  }
  if (props.error instanceof UnauthorizedError) return <UnauthorizedPage {...props} />;
  throw props.error;
}

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: DefaultErrorComponent,
  defaultPendingComponent: () => <LoadingPageComponent isFullScreen />,
  context: {
    queryClient,
    auth: undefined!,
    institutionId: undefined!,
    portfolioId: undefined!,
  },
});
// TypeScript module augmentation
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    breadcrumb?: {
      title: string | ((params: StaticDataBreadcrumbsTitleFunctionParams) => string);
      icon?: Icon;
    };
    /**
     * Determines if the breadcrumb trail should be displayed on the current page. Defaults to true
     */
    displayBreadcrumbs?: boolean;
  }
}

function AppContent() {
  const auth = useAuth();

  return <RouterProvider router={router} context={{ auth }} />;
}

function App() {
  useEffect(() => {
    // Initialize route tracking only if Datadog is initialized
    if (isDatadogInitialized()) {
      initializeRouteTracking(router);
    }
  }, []);

  queryClient.setQueryDefaults(useUserDataQuery.getKey(), {
    staleTime: 0,
  });

  return (
    <PaxProvider
      themeName="gax"
      themeOptions={paxCombinedTheme}
      globalTranslations={{
        en_US: sharedLocale,
      }}
      locale="en_US"
    >
      <GlobalStyles
        styles={{
          // Scope the lowered z-index to THIS provider only (via its containerRoot
          // class) so the sidebar drawer (zIndex 1200) clips the toast as it slides
          // out. Other snackbar providers keep notistack's default 1400.
          ".sidebar-aware-snackbar": { zIndex: "auto !important", position: "absolute" },
        }}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <PaxSnackbarProvider
          TransitionComponent={SidebarSlideTransition}
          classes={{ containerRoot: "sidebar-aware-snackbar" }}
          onEnter={(node) => {
            if (node.parentElement) node.parentElement.style.zIndex = "1150";
            // Underneath sidebar when sliding in
          }}
          onEntered={(node) => {
            if (node.parentElement) node.parentElement.style.zIndex = "1400";
            // Above modal once transition completes
          }}
          onExit={(node) => {
            if (node.parentElement) node.parentElement.style.zIndex = "1150";
            // Underneath sidebar when sliding out
          }}
          Components={{
            default: MainContentSnackbarAlert,
            success: MainContentSnackbarAlert,
            error: MainContentSnackbarAlert,
            warning: MainContentSnackbarAlert,
            info: MainContentSnackbarAlert,
          }}
        >
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <AppContent />
            </QueryClientProvider>
          </AuthProvider>
        </PaxSnackbarProvider>
      </LocalizationProvider>
    </PaxProvider>
  );
}

export default App;
